import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  Users, TrendingUp, TrendingDown, Trophy, Target, DollarSign, Copy, Plus, UserPlus, Percent
} from 'lucide-react';

const CopyTrading = () => {
  const [providers, setProviders] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isFollowDialogOpen, setIsFollowDialogOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  
  const [newProvider, setNewProvider] = useState({
    name: '',
    description: '',
    strategy: '',
    min_investment: 100,
    performance_fee: 20
  });
  
  const [followData, setFollowData] = useState({
    investment_amount: 100,
    copy_ratio: 1.0,
    max_drawdown_limit: 30
  });

  const fetchData = async () => {
    try {
      const [providersRes, subsRes] = await Promise.all([
        api.getCopyProviders(),
        api.getMySubscriptions()
      ]);
      setProviders(providersRes.data);
      setSubscriptions(subsRes.data);
    } catch (error) {
      console.error('Failed to load copy trading data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateProvider = async (e) => {
    e.preventDefault();
    try {
      await api.createSignalProvider(newProvider);
      toast.success('Signal provider profile created!');
      setIsCreateDialogOpen(false);
      setNewProvider({ name: '', description: '', strategy: '', min_investment: 100, performance_fee: 20 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create provider');
    }
  };

  const handleFollow = async (e) => {
    e.preventDefault();
    if (!selectedProvider) return;
    
    try {
      await api.followProvider({
        provider_id: selectedProvider.id,
        ...followData
      });
      toast.success(`Now following ${selectedProvider.name}!`);
      setIsFollowDialogOpen(false);
      setSelectedProvider(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to follow provider');
    }
  };

  const handleUnfollow = async (subscriptionId) => {
    try {
      await api.unfollowProvider(subscriptionId);
      toast.success('Unfollowed successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to unfollow');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="copy-trading-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Copy Trading</h1>
          <p className="text-muted-foreground">Follow top traders and copy their strategies</p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="glow-primary" data-testid="become-provider-btn">
              <Trophy className="w-4 h-4 mr-2" />
              Become a Provider
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Signal Provider Profile</DialogTitle>
              <DialogDescription>Share your trading strategies and earn performance fees</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateProvider} className="space-y-4">
              <div className="space-y-2">
                <Label>Strategy Name</Label>
                <Input
                  value={newProvider.name}
                  onChange={(e) => setNewProvider({ ...newProvider, name: e.target.value })}
                  placeholder="e.g., Scalping Master"
                  required
                  data-testid="provider-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Strategy Type</Label>
                <Input
                  value={newProvider.strategy}
                  onChange={(e) => setNewProvider({ ...newProvider, strategy: e.target.value })}
                  placeholder="e.g., Scalping, Swing Trading, Day Trading"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={newProvider.description}
                  onChange={(e) => setNewProvider({ ...newProvider, description: e.target.value })}
                  placeholder="Describe your trading strategy..."
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Min Investment ($)</Label>
                  <Input
                    type="number"
                    value={newProvider.min_investment}
                    onChange={(e) => setNewProvider({ ...newProvider, min_investment: parseFloat(e.target.value) })}
                    min={100}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Performance Fee (%)</Label>
                  <Input
                    type="number"
                    value={newProvider.performance_fee}
                    onChange={(e) => setNewProvider({ ...newProvider, performance_fee: parseFloat(e.target.value) })}
                    min={0}
                    max={50}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="submit-provider-btn">Create Profile</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="providers" className="space-y-6">
        <TabsList>
          <TabsTrigger value="providers" data-testid="tab-providers">
            <Users className="w-4 h-4 mr-2" />
            Signal Providers
          </TabsTrigger>
          <TabsTrigger value="subscriptions" data-testid="tab-subscriptions">
            <Copy className="w-4 h-4 mr-2" />
            My Subscriptions
          </TabsTrigger>
        </TabsList>

        {/* Signal Providers Tab */}
        <TabsContent value="providers">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {providers.length === 0 ? (
              <Card className="col-span-full">
                <CardContent className="py-12 text-center text-muted-foreground">
                  <Trophy className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No signal providers yet</p>
                  <p className="text-sm">Be the first to share your strategy!</p>
                </CardContent>
              </Card>
            ) : (
              providers.map((provider) => (
                <Card key={provider.id} className="card-hover" data-testid={`provider-card-${provider.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{provider.name}</CardTitle>
                        <CardDescription>{provider.strategy}</CardDescription>
                      </div>
                      <Badge variant="outline" className="text-primary border-primary/30">
                        {provider.total_followers} followers
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground line-clamp-2">{provider.description}</p>
                    
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="p-2 rounded bg-muted/50">
                        <p className="text-xs text-muted-foreground">Win Rate</p>
                        <p className="font-mono font-medium text-emerald-400">{provider.win_rate}%</p>
                      </div>
                      <div className="p-2 rounded bg-muted/50">
                        <p className="text-xs text-muted-foreground">Monthly Return</p>
                        <p className={`font-mono font-medium ${provider.monthly_return >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {provider.monthly_return >= 0 ? '+' : ''}{provider.monthly_return}%
                        </p>
                      </div>
                      <div className="p-2 rounded bg-muted/50">
                        <p className="text-xs text-muted-foreground">Max Drawdown</p>
                        <p className="font-mono font-medium text-amber-400">{provider.max_drawdown}%</p>
                      </div>
                      <div className="p-2 rounded bg-muted/50">
                        <p className="text-xs text-muted-foreground">Total Profit</p>
                        <p className="font-mono font-medium">{formatCurrency(provider.total_profit)}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-border">
                      <div className="text-sm">
                        <span className="text-muted-foreground">Min: </span>
                        <span className="font-mono">{formatCurrency(provider.min_investment)}</span>
                      </div>
                      <Button 
                        size="sm"
                        onClick={() => {
                          setSelectedProvider(provider);
                          setFollowData({ ...followData, investment_amount: provider.min_investment });
                          setIsFollowDialogOpen(true);
                        }}
                        data-testid={`follow-btn-${provider.id}`}
                      >
                        <UserPlus className="w-4 h-4 mr-1" />
                        Follow
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Subscriptions Tab */}
        <TabsContent value="subscriptions">
          <Card>
            <CardHeader>
              <CardTitle>Active Subscriptions</CardTitle>
            </CardHeader>
            <CardContent>
              {subscriptions.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground">
                  <Copy className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No active subscriptions</p>
                  <p className="text-sm">Follow a signal provider to start copy trading</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Provider</TableHead>
                      <TableHead>Investment</TableHead>
                      <TableHead>Copy Ratio</TableHead>
                      <TableHead>Current P&L</TableHead>
                      <TableHead>Trades Copied</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subscriptions.map((sub) => (
                      <TableRow key={sub.id}>
                        <TableCell className="font-medium">{sub.provider_id.slice(0, 8)}...</TableCell>
                        <TableCell className="font-mono">{formatCurrency(sub.investment_amount)}</TableCell>
                        <TableCell className="font-mono">{sub.copy_ratio}x</TableCell>
                        <TableCell className={`font-mono ${sub.current_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {sub.current_profit >= 0 ? '+' : ''}{formatCurrency(sub.current_profit)}
                        </TableCell>
                        <TableCell className="font-mono">{sub.total_copied_trades}</TableCell>
                        <TableCell>
                          <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={() => handleUnfollow(sub.id)}
                          >
                            Unfollow
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Follow Dialog */}
      <Dialog open={isFollowDialogOpen} onOpenChange={setIsFollowDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Follow {selectedProvider?.name}</DialogTitle>
            <DialogDescription>Configure your copy trading settings</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleFollow} className="space-y-4">
            <div className="space-y-2">
              <Label>Investment Amount ($)</Label>
              <Input
                type="number"
                value={followData.investment_amount}
                onChange={(e) => setFollowData({ ...followData, investment_amount: parseFloat(e.target.value) })}
                min={selectedProvider?.min_investment || 100}
                data-testid="follow-investment-input"
              />
              <p className="text-xs text-muted-foreground">Minimum: {formatCurrency(selectedProvider?.min_investment || 100)}</p>
            </div>
            <div className="space-y-2">
              <Label>Copy Ratio</Label>
              <Input
                type="number"
                step="0.1"
                value={followData.copy_ratio}
                onChange={(e) => setFollowData({ ...followData, copy_ratio: parseFloat(e.target.value) })}
                min={0.1}
                max={5}
              />
              <p className="text-xs text-muted-foreground">1.0 = exact copy, 0.5 = half volume, 2.0 = double volume</p>
            </div>
            <div className="space-y-2">
              <Label>Max Drawdown Limit (%)</Label>
              <Input
                type="number"
                value={followData.max_drawdown_limit}
                onChange={(e) => setFollowData({ ...followData, max_drawdown_limit: parseFloat(e.target.value) })}
                min={5}
                max={100}
              />
              <p className="text-xs text-muted-foreground">Stop copying if drawdown exceeds this percentage</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsFollowDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="confirm-follow-btn">Start Copying</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CopyTrading;
